import React, { useState } from 'react';
import { Button, Chip, Notice, Panel } from '@origo/origo-ui';
import { MapTrifoldIcon, StackPlusIcon } from '@phosphor-icons/react';
import { useDatasetSchema } from '../../api/hooks';
import { PanelError, PanelLoading } from '../common/PanelStates';
import { useDatasetPreview } from '../common/useDatasetPreview';
import { ProvisionDialog } from './ProvisionDialog';
import { FIELD_DATA_TYPE_LABEL, type DatasetSummary } from '../../api/types';
import styles from '../common/panel.module.css';

interface DatasetDetailProps {
    system: string;
    dataset: DatasetSummary;
    onBack: () => void;
}

export const DatasetDetail: React.FC<DatasetDetailProps> = ({ system, dataset, onBack }) => {
    const { data: schema, isLoading, isError, error, refetch } = useDatasetSchema(system, dataset.datasetId);
    const { preview, isLoading: previewLoading, error: previewError } = useDatasetPreview();
    const [provisionOpen, setProvisionOpen] = useState(false);

    return (
        <Panel heading={dataset.name} onBack={onBack} flush>
            <div className={styles.scroll}>
                <div className={styles.chipRow}>
                    {dataset.geometryType && <Chip variant="info">{dataset.geometryType}</Chip>}
                    {dataset.crs && <Chip>{dataset.crs}</Chip>}
                </div>

                <div className={styles.metaGrid}>
                    <span className={styles.metaLabel}>Datasett-ID</span>
                    <span className={styles.metaValue} style={{ fontFamily: 'var(--font-mono)' }}>{dataset.datasetId}</span>
                    {dataset.description && (
                        <>
                            <span className={styles.metaLabel}>Beskrivelse</span>
                            <span className={styles.metaValue}>{dataset.description}</span>
                        </>
                    )}
                </div>

                <div className={styles.rowActions}>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<MapTrifoldIcon size={16} weight="bold" />}
                        onClick={() => preview(system, dataset.datasetId, dataset.name)}
                        disabled={previewLoading}
                    >
                        {previewLoading ? 'Henter…' : 'Vis i kart'}
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<StackPlusIcon size={16} weight="bold" />}
                        onClick={() => setProvisionOpen(true)}
                    >
                        Provisjoner AGOL-tjeneste
                    </Button>
                </div>

                {previewError && <Notice variant="warning">{previewError}</Notice>}

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Skjema</h3>
                    {isLoading && <PanelLoading label="Laster skjema…" />}
                    {isError && <PanelError error={error} onRetry={() => refetch()} />}
                    {schema && (
                        <>
                            <div className={styles.metaGrid}>
                                <span className={styles.metaLabel}>Geometri</span>
                                <span className={styles.metaValue}>{schema.geometryType}</span>
                                {schema.spatialReferenceWkid != null && (
                                    <>
                                        <span className={styles.metaLabel}>EPSG</span>
                                        <span className={styles.metaValue}>{schema.spatialReferenceWkid}</span>
                                    </>
                                )}
                                <span className={styles.metaLabel}>Antall felt</span>
                                <span className={styles.metaValue}>{schema.fields.length}</span>
                            </div>

                            <div className={styles.list} style={{ marginTop: 'var(--space-02)' }}>
                                {schema.fields.map((f) => (
                                    <div
                                        key={f.name}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 'var(--space-02)',
                                            padding: 'var(--space-02) 0',
                                            borderBottom: '1px solid var(--border-default)',
                                            fontSize: 'var(--text-sm)',
                                        }}
                                    >
                                        <span style={{ fontFamily: 'var(--font-mono)' }}>{f.name}</span>
                                        <span style={{ color: 'var(--text-primary-70)' }}>
                                            {FIELD_DATA_TYPE_LABEL[f.dataType] ?? f.dataType}
                                            {!f.nullable ? ' *' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ProvisionDialog open={provisionOpen} onClose={() => setProvisionOpen(false)} system={system} dataset={dataset} />
        </Panel>
    );
};
